import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [dismissed, setDismissed] = useState(false);
    const [isIos, setIsIos] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        const standalone = window.matchMedia('(display-mode: standalone)').matches
            || ('standalone' in window.navigator && (window.navigator as unknown as { standalone: boolean }).standalone);
        setIsStandalone(standalone);

        const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
        setIsIos(ios);

        const handler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };

        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    useEffect(() => {
        const d = localStorage.getItem('pwa-install-dismissed');
        if (d) {
            const dismissedAt = parseInt(d, 10);
            // Show again after 7 days
            if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) {
                setDismissed(true);
            }
        }
    }, []);

    if (isStandalone || dismissed) return null;

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setDeferredPrompt(null);
        }
    };

    const handleDismiss = () => {
        setDismissed(true);
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    };

    // Android / Chrome install prompt
    if (deferredPrompt) {
        return (
            <div className="fixed bottom-16 left-4 right-4 z-50 rounded-lg border border-violet-800/50 bg-slate-900 p-3 shadow-xl lg:bottom-4 lg:left-auto lg:right-4 lg:w-80">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-900/50">
                        <Download className="h-5 w-5 text-violet-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">Install DebtBound</p>
                        <p className="mt-0.5 text-xs text-slate-400">
                            Add to your home screen for the best experience
                        </p>
                        <div className="mt-2 flex gap-2">
                            <Button size="sm" onClick={handleInstall} className="bg-violet-600 hover:bg-violet-700">
                                Install
                            </Button>
                            <Button size="sm" variant="ghost" onClick={handleDismiss}>
                                Not now
                            </Button>
                        </div>
                    </div>
                    <button onClick={handleDismiss} className="text-slate-500 hover:text-slate-300">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        );
    }

    // iOS Safari install instructions
    if (isIos) {
        return (
            <div className="fixed bottom-16 left-4 right-4 z-50 rounded-lg border border-violet-800/50 bg-slate-900 p-3 shadow-xl lg:bottom-4 lg:left-auto lg:right-4 lg:w-80">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-900/50">
                        <Download className="h-5 w-5 text-violet-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">Install DebtBound</p>
                        <p className="mt-0.5 text-xs text-slate-400">
                            Tap the share button <span className="inline-block translate-y-0.5">&#x2191;&#xFE0E;</span> then "Add to Home Screen"
                        </p>
                    </div>
                    <button onClick={handleDismiss} className="text-slate-500 hover:text-slate-300">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        );
    }

    return null;
}
